// client/src/components/influencer/InfluencerActivityHome.js
// 활동 홈 — 인플루언서 마이비즈 첫 화면 (시안 redesign-v4-ibiz-final.html)
// 기존 목록 API를 조합해 "지금 할 일"과 진행 중인 활동(6단계 파이프라인)을 보여준다. 신규 서버 API 없음.
// 마케터 비즈 홈(MarketerBizHome)과 동일 구조 — mbh-* 클래스 재사용(ib-theme 민트 보정).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PROJECT_CREATION_PAUSED, V2_UPDATE_DATE_TEXT } from '../../config/maintenance';
import { usePoints } from '../../contexts/PointContext';
import '../../styles/marketer/mb-home.css';

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
const STAGE_COUNT = 6; // 지원 → 선정 → 계약 → 제출 → 검수 → 지급

const SERVICE_LABEL = { delivery: '배송형 (Product provided)', purchase: '구매형 (Self-purchase)', visit: '방문형 (Store visit)', free: '자율형 (Flexible)' };

function dday(date) {
  if (!date) return null;
  const diff = new Date(date).getTime() - Date.now();
  return Math.ceil(diff / (24 * 60 * 60 * 1000));
}

function shortDate(date) {
  if (!date) return '';
  const d = new Date(date);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

function productNameOf(p) {
  return p.productName || p.product?.name || p.product?.placeName || p.product?.customTitle || '광고 프로젝트';
}

function rewardLabel(p) {
  const parts = [];
  if (p.rewardPointPerCreator > 0) parts.push(`보상 (Reward) ${Number(p.rewardPointPerCreator).toLocaleString()}P`);
  if (p.perPurchaseReward?.use) parts.push(`구매당 (Per sale) +${Number(p.perPurchaseReward.rewardPerBuyer || 0).toLocaleString()}P`);
  return parts.join(' · ');
}

function Pipeline({ stage }) {
  return (
    <span className="mbh-pipe" aria-hidden="true">
      {Array.from({ length: STAGE_COUNT }, (_, i) => {
        const cls = i + 1 < stage ? 'is-done' : i + 1 === stage ? 'is-now' : '';
        return <i key={i} className={cls} />;
      })}
    </span>
  );
}

function InfluencerActivityHome() {
  const nav = useNavigate();
  const { points, loading: pointsLoading } = usePoints();

  const [resultPending, setResultPending] = useState(null); // null = 로딩 중
  const [selected, setSelected] = useState([]);
  const [pendingApps, setPendingApps] = useState([]);
  const [openProjects, setOpenProjects] = useState([]);
  const [pendingExchange, setPendingExchange] = useState(0);
  const [notices, setNotices] = useState([]);
  const [error, setError] = useState(false);

  const go = useCallback((sub) => nav(`/influencer/mybiz?sub=${sub}`), [nav]);

  // 새 소식 (구 /influencer/home의 공지 영역 흡수)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/notices?page=1&pageSize=3', { credentials: 'include' });
        const data = await res.json().catch(() => ({}));
        if (!cancelled && data.ok) setNotices(data.items || []);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  const load = useCallback(async () => {
    setError(false);
    setResultPending(null);
    try {
      const [resultRes, selectedRes, appsRes, openRes, exchangeRes] = await Promise.all([
        fetch('/api/influencer/mybiz/projects/result-pending', { credentials: 'include' }),
        fetch('/api/influencer/mybiz/projects/selected', { credentials: 'include' }),
        fetch('/api/influencer/projects/my-applications?status=pending&limit=50', { credentials: 'include' }),
        fetch('/api/influencer/mybiz/projects/available?limit=3&sort=created', { credentials: 'include' }),
        fetch('/api/influencer/mybiz/points/exchange/status', { credentials: 'include' }),
      ]);
      const result = await resultRes.json().catch(() => ({}));
      const sel = await selectedRes.json().catch(() => ({}));
      const apps = await appsRes.json().catch(() => ({}));
      const open = await openRes.json().catch(() => ({}));
      const exchange = await exchangeRes.json().catch(() => ({}));

      if (!resultRes.ok || !result.ok) throw new Error('result-pending');

      setResultPending(result.projects || []);
      setSelected(sel.ok ? sel.projects || [] : []);
      setPendingApps(apps.ok ? apps.items || [] : []);
      setOpenProjects(open.ok ? open.items || [] : []);
      setPendingExchange(exchange.ok ? Number(exchange.pendingExchangePoints || 0) : 0);
    } catch {
      setResultPending([]);
      setError(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── 할 일 집계 ──
  const todos = useMemo(() => {
    if (!resultPending) return [];
    const items = [];

    // 1) 결과물 제출 대기 (미제출 · 마감 임박 순 — 서버 정렬 유지)
    const toSubmit = resultPending.filter((p) => p.resultStatus === 'none' || p.resultStatus === 'expired');
    if (toSubmit.length > 0) {
      const first = toSubmit[0];
      const d = first.daysUntilDeadline;
      items.push({
        key: 'submit', ico: '⏰', mood: 'amber',
        title: first.isExpired
          ? <>제출 마감이 지난 결과물이 있어요 (Overdue deliverable)</>
          : <>결과물 제출 마감 {d != null ? <b>D-{d}</b> : null} (Deliverable due)</>,
        sub: `${productNameOf(first)}${toSubmit.length > 1 ? ` 외 ${toSubmit.length - 1}건 (+${toSubmit.length - 1} more)` : ''}`,
        action: '제출하기 (Submit)', onClick: () => go('resultSend'),
      });
    }

    // 2) 선정 → 계약 응답 대기
    if (selected.length > 0) {
      const first = selected[0];
      const d = first.daysLeft;
      const deadline = d == null ? '' : d <= 0 ? '오늘까지 (today)' : d === 1 ? '내일까지 (tomorrow)' : `D-${d}`;
      items.push({
        key: 'contract', ico: '🎉', mood: 'mint',
        title: <>선정됐어요! 계약 응답 대기 <b>{selected.length}건</b> (Selected — contracts to answer: {selected.length})</>,
        sub: `${productNameOf(first)}${deadline ? ` · 응답 기한 (Reply by) ${deadline}` : ''}`,
        action: '계약 확인 (View Contract)', onClick: () => go('projectContract'),
      });
    }

    // 3) 수정 요청 받은 결과물
    const revisions = resultPending.filter((p) => p.resultStatus === 'rejected' || p.resultStatus === 'admin_rejected');
    if (revisions.length > 0) {
      items.push({
        key: 'revision', ico: '✍️', mood: 'red',
        title: <>수정 요청 받은 결과물 <b>{revisions.length}건</b> (Revisions requested: {revisions.length})</>,
        sub: productNameOf(revisions[0]),
        action: '수정하기 (Revise)', onClick: () => go('resultSend'),
      });
    }

    return items;
  }, [resultPending, selected, go]);

  // ── 진행 중인 활동 카드 (급한 순: 제출/수정 → 계약 응답 → 지원 심사) ──
  const cards = useMemo(() => {
    if (!resultPending) return [];
    const list = [];

    resultPending.forEach((p) => {
      const isRevision = p.resultStatus === 'rejected' || p.resultStatus === 'admin_rejected';
      const isSubmitted = p.resultStatus === 'submitted';
      // 진행 카드의 단계·단위 라벨은 고정폭 컬럼(132px/86px) 오버플로로 병기 제외 — 카드 제목·섹션 제목 병기는 유지
      const stage = isSubmitted ? { n: 5, label: '검수 중' } : isRevision ? { n: 4, label: '수정 제출' } : { n: 4, label: '결과물 제출' };
      let num; let unit; let numClass = '';
      if (isSubmitted) { num = shortDate(p.resultSubmittedAt); unit = '제출 완료'; }
      else if (p.isExpired) { num = '마감 지남'; unit = '제출 마감'; numClass = 'is-red'; }
      else { num = p.daysUntilDeadline != null ? `D-${p.daysUntilDeadline}` : '-'; unit = isRevision ? '수정 요청 (Revision)' : '제출 마감 (Due)'; numClass = isRevision ? 'is-red' : 'is-amber'; }
      list.push({
        id: `result-${p.applicationId}`, name: productNameOf(p), cover: p.coverImage,
        sub: [SERVICE_LABEL[p.serviceType] || p.serviceType, rewardLabel(p)].filter(Boolean).join(' · '),
        stage, num, unit, numClass, onClick: () => go('resultSend'),
      });
    });

    selected.forEach((p) => {
      const d = p.daysLeft;
      list.push({
        id: `contract-${p.applicationId}`, name: productNameOf(p), cover: p.coverImage,
        sub: [SERVICE_LABEL[p.serviceType] || p.serviceType, rewardLabel(p)].filter(Boolean).join(' · '),
        stage: { n: 3, label: '계약 응답' },
        num: d == null ? '-' : d <= 0 ? '오늘 (today)' : d === 1 ? '내일 (tomorrow)' : `D-${d}`,
        unit: '응답 기한', numClass: 'is-mint', onClick: () => go('projectContract'),
      });
    });

    pendingApps.forEach((a) => {
      const p = a.project || {};
      list.push({
        id: `apply-${a.id}`, name: productNameOf(p), cover: p.cover,
        sub: [SERVICE_LABEL[p.serviceType] || p.serviceType, rewardLabel(p)].filter(Boolean).join(' · '),
        stage: { n: 1, label: '지원 심사 중' },
        num: shortDate(p.recruitEnd), unit: '발표 예정', numClass: '', onClick: () => go('projectApplyCheck'),
      });
    });

    return list;
  }, [resultPending, selected, pendingApps, go]);

  const today = new Date();
  const dateLabel = `${today.getMonth() + 1}월 ${today.getDate()}일 ${WEEKDAYS[today.getDay()]}요일`;
  const loading = resultPending === null;

  return (
    <div className="mbh">
      <div className="mbh-grid">
        {/* 활동 브리핑 */}
        <section className="mbh-brief" aria-label="지금 할 일">
          <div className="mbh-date">{dateLabel}</div>
          <h2 className="mbh-title">
            {loading ? '할 일을 확인하고 있어요 (Checking your tasks…)' : todos.length > 0
              ? <>지금 할 일이 <b>{todos.length}건</b> 있어요 <span style={{ color: '#6b7686', fontWeight: 500 }}>(Tasks to do: {todos.length})</span></>
              : '지금 할 일이 없어요 (No tasks right now)'}
          </h2>

          {loading && <div className="mbh-loading">불러오는 중… (Loading…)</div>}

          {!loading && error && (
            <div className="mbh-error">
              데이터를 불러오지 못했어요. (Failed to load.)
              <button type="button" onClick={load}>다시 시도 (Retry)</button>
            </div>
          )}

          {!loading && !error && todos.length === 0 && (
            <div className="mbh-todo__empty">모든 할 일을 처리했어요. 새 프로젝트에 지원해보세요! ✨ (All done — try applying to a new project!)</div>
          )}

          {!loading && !error && todos.length > 0 && (
            <div className="mbh-todo">
              {todos.map((t) => (
                <button key={t.key} type="button" className="mbh-todo__item" onClick={t.onClick}>
                  <span className={`mbh-todo__ico mbh-todo__ico--${t.mood}`} aria-hidden="true">{t.ico}</span>
                  <span className="mbh-todo__meta">
                    <span className="mbh-todo__t">{t.title}</span>
                    <span className="mbh-todo__s">{t.sub}</span>
                  </span>
                  <span className="mbh-todo__go">{t.action} →</span>
                </button>
              ))}
            </div>
          )}
        </section>

        {/* 포인트 월렛 */}
        <div>
          <section className="mbh-wallet" aria-label="보유 포인트">
            <div className="mbh-wallet__label">보유 포인트 (Points Balance)</div>
            <div className="mbh-wallet__amt">
              {pointsLoading ? '…' : Number(points || 0).toLocaleString()}<small>P</small>
            </div>
            <div className="mbh-wallet__sub">
              {pendingExchange > 0
                ? `환전 신청 중 ${pendingExchange.toLocaleString()}P 별도 (Withdrawal pending)`
                : loading ? ' ' : `진행 중인 활동 ${cards.length}건 (Active: ${cards.length})`}
            </div>
            <div className="mbh-wallet__cta">
              <button type="button" className="mbh-btn-white" onClick={() => go('pointExchange')}>환전 신청 (Withdraw)</button>
              <button type="button" className="mbh-btn-glass" onClick={() => go('pointList')}>포인트 내역 (History)</button>
            </div>
          </section>

          {/* 새 소식: 공지 0건이어도 카드·전체 보기 진입점은 항상 노출 */}
          <section className="mbh-spend" aria-label="새 소식" style={{ marginBottom: 16 }}>
            <div className="mbh-spend__row" style={{ paddingTop: 2 }}>
              <span style={{ fontWeight: 800, color: 'var(--mb-text-title)', fontSize: 14 }}>새 소식 (News)</span>
              <b>
                <button
                  type="button"
                  onClick={() => {
                    try { sessionStorage.setItem('current_user_role', 'influencer'); } catch {}
                    nav('/notice', { state: { role: 'influencer' } });
                  }}
                  style={{ border: 0, background: 'none', color: 'var(--mb-color-primary-dark)', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, padding: 0 }}
                >
                  전체 보기 (View all) →
                </button>
              </b>
            </div>
            {notices.length === 0 && (
              <div className="mbh-spend__row"><span>새로운 공지가 없어요 (No new notices)</span></div>
            )}
            {notices.map((n) => (
              <div className="mbh-spend__row" key={n._id} style={{ gap: 12 }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, color: 'var(--mb-text-title)', fontWeight: 600 }}>
                  {n.important ? '📌 ' : ''}{n.title}
                </span>
                <b>
                  <button
                    type="button"
                    onClick={() => nav(`/notice/${n._id}`)}
                    style={{ border: 0, background: 'none', color: 'var(--mb-color-primary-dark)', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit', padding: 0, whiteSpace: 'nowrap' }}
                  >
                    보기 (View) →
                  </button>
                </b>
              </div>
            ))}
          </section>

          {/* (고도화 예정) 시안의 활동 성과 스탯 스트립(완료 수·만족도·기한 내 제출률) 자리 — 완료 프로젝트 집계 API 마련 후 복원 */}
        </div>
      </div>

      {/* 진행 중인 활동 */}
      <section className="mb-card" aria-label="진행 중인 활동">
        <h3 style={{ fontSize: '15.5px', fontWeight: 800, letterSpacing: '-0.01em', marginBottom: 14 }}>진행 중인 활동 (Ongoing Activities)</h3>
        {loading && <div className="mbh-loading">불러오는 중… (Loading…)</div>}
        {!loading && cards.length === 0 && (
          <div className="mbh-todo__empty">진행 중인 활동이 없어요. 새 프로젝트에 지원해보세요. (No ongoing activities — try applying to a new project.)</div>
        )}
        {!loading && cards.length > 0 && (
          <div className="mbh-plist">
            {cards.map((c) => (
              <button key={c.id} type="button" className="mbh-pcard" onClick={c.onClick}>
                <span className="mbh-pcard__logo" aria-hidden="true">
                  {c.cover ? <img src={c.cover} alt="" /> : (c.name || '프').charAt(0)}
                </span>
                <span className="mbh-pcard__meta">
                  <span className="mbh-pcard__name">{c.name}</span>
                  <span className="mbh-pcard__sub">{c.sub}</span>
                </span>
                <span className="mbh-pcard__pipe">
                  <Pipeline stage={c.stage.n} />
                  <span className="mbh-pcard__stage">{c.stage.n}단계 · {c.stage.label}</span>
                </span>
                <span className="mbh-pcard__stat">
                  <span className={`mbh-pcard__num ${c.numClass}`}>{c.num}</span>
                  <span className="mbh-pcard__unit">{c.unit}</span>
                </span>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* 새로 올라온 프로젝트 */}
      <section className="mb-card" aria-label="새로 올라온 프로젝트">
        <h3 style={{ fontSize: '15.5px', fontWeight: 800, letterSpacing: '-0.01em', marginBottom: 6 }}>
          새로 올라온 프로젝트 (New Projects)
          <button
            type="button"
            onClick={() => go('projectApply')}
            style={{ float: 'right', border: 0, background: 'none', color: 'var(--mb-color-primary-dark)', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, padding: 0 }}
          >
            더 보기 (More) →
          </button>
        </h3>
        {openProjects.length === 0 && (
          <div className="mbh-spend__row"><span>{loading ? '불러오는 중… (Loading…)' : '모집 중인 프로젝트가 없어요 (No projects recruiting)'}</span></div>
        )}
        {/* v2 전환: 차단 기간 빈 상태 안내 (10/10 배포 시 제거) */}
        {!loading && openProjects.length === 0 && PROJECT_CREATION_PAUSED && (
          <p style={{ marginTop: 8, fontSize: 13, color: '#8a90a0' }}>
            {V2_UPDATE_DATE_TEXT} 업데이트 준비 기간이에요. 새 프로젝트는 업데이트 이후 순차적으로 열립니다.
          </p>
        )}
        {openProjects.map((p) => {
          const d = dday(p.recruitEnd);
          return (
            <div className="mbh-spend__row" key={p.id} style={{ gap: 12 }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, color: 'var(--mb-text-title)', fontWeight: 600 }}>
                {productNameOf(p)}
                <span style={{ color: 'var(--mb-text-sub)', fontWeight: 500 }}>
                  {` · ${[SERVICE_LABEL[p.serviceType] || p.serviceType, rewardLabel(p)].filter(Boolean).join(' · ')}`}
                </span>
              </span>
              {p.isApplied
                ? <span className="mb-badge mb-badge-gray">지원함 (Applied)</span>
                : d != null && <span className="mb-badge mb-badge-green">D-{Math.max(0, d)}</span>}
              <b>
                <button
                  type="button"
                  onClick={() => go('projectApply')}
                  style={{ border: 0, background: 'none', color: 'var(--mb-color-primary-dark)', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit', padding: 0, whiteSpace: 'nowrap' }}
                >
                  보기 (View) →
                </button>
              </b>
            </div>
          );
        })}
      </section>
    </div>
  );
}

export default InfluencerActivityHome;
